const MAX_EDGE_PX = 1600;
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_SOURCE_PIXELS = 25_000_000;
const JPEG_QUALITY = 0.82;
const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('无法读取图片，请改用 JPG、PNG 或 WebP 格式。'));
    image.src = url;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('图片压缩失败，请重新选择图片。')),
      'image/jpeg',
      quality,
    );
  });
}

/**
 * AI 快拍上传前处理：浏览器解码会应用 EXIF 朝向，重新绘制后得到方向正确的小尺寸 JPEG。
 */
export async function prepareAiSnapImage(file: File): Promise<File> {
  const mime = (file.type || '').toLowerCase();
  if (!SUPPORTED_TYPES.has(mime)) {
    throw new Error('AI 快拍仅支持 JPG、PNG 或 WebP 图片。');
  }
  if (file.size <= 0) {
    throw new Error('图片文件为空，请重新选择。');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;
    if (!sourceWidth || !sourceHeight) {
      throw new Error('图片尺寸无效，请重新选择。');
    }
    if (sourceWidth * sourceHeight > MAX_SOURCE_PIXELS) {
      throw new Error('图片像素过大，请裁剪后重试。');
    }

    const scale = Math.min(1, MAX_EDGE_PX / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('当前设备无法处理图片，请重试。');
    }

    // JPEG 不支持透明通道；白底可避免透明 PNG 被转为黑底。
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    let blob = await canvasToJpeg(canvas, JPEG_QUALITY);
    if (blob.size > MAX_UPLOAD_BYTES) {
      blob = await canvasToJpeg(canvas, 0.65);
    }
    if (blob.size > MAX_UPLOAD_BYTES) {
      throw new Error('图片压缩后仍超过 8MB，请裁剪后重试。');
    }

    const baseName = (file.name || 'ai-snap').replace(/\.[^.]+$/, '') || 'ai-snap';
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
